import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Building2, Briefcase, CheckCircle, XCircle, Clock, Ban,
  Search, Loader2, Mail, Phone, Globe, DollarSign, FileText, X
} from 'lucide-react';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminB2BPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editPartner, setEditPartner] = useState(null);
  const [editForm, setEditForm] = useState({ commission_rate: 0, discount_percentage: 0, notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchPartners = async () => {
    try {
      const res = await api.get('/admin/partners', { params: { status: filter !== 'all' ? filter : undefined } });
      setPartners(res.data);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPartners(); }, [filter]);

  const handleAction = async (userId, action) => {
    try {
      await api.put(`/admin/partners/${userId}`, { approval_status: action });
      toast.success(`Partner ${action} successfully`);
      fetchPartners();
    } catch (err) {
      toast.error('Failed to update partner');
    }
  };

  const handleSaveSettings = async () => {
    if (!editPartner) return;
    setSaving(true);
    try {
      await api.put(`/admin/partners/${editPartner.user_id}`, editForm);
      toast.success('Partner settings updated');
      setEditPartner(null);
      fetchPartners();
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (partner) => {
    setEditPartner(partner);
    setEditForm({
      commission_rate: partner.commission_rate || 0,
      discount_percentage: partner.discount_percentage || 0,
      notes: partner.notes || ''
    });
  };

  const filtered = partners.filter(p => {
    if (!search) return true;
    return p.company_name?.toLowerCase().includes(search.toLowerCase()) ||
           p.name?.toLowerCase().includes(search.toLowerCase()) ||
           p.email?.toLowerCase().includes(search.toLowerCase());
  });

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500 text-white',
      approved: 'bg-green-500 text-white',
      rejected: 'bg-red-500 text-white',
      suspended: 'bg-gray-700 text-white',
    };
    return styles[status] || 'bg-gray-400 text-white';
  };

  const counts = {
    all: partners.length,
    pending: partners.filter(p => p.approval_status === 'pending').length,
    approved: partners.filter(p => p.approval_status === 'approved').length,
    suspended: partners.filter(p => p.approval_status === 'suspended').length,
    rejected: partners.filter(p => p.approval_status === 'rejected').length,
  };

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar role="admin" />
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="admin-b2b-page">
        <h1 className="text-2xl font-bold text-[#0A1E40] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Partners Management
        </h1>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input placeholder="Search partners..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="b2b-search" />
          </div>
          {['all', 'pending', 'approved', 'suspended', 'rejected'].map(s => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? 'default' : 'outline'}
              onClick={() => setFilter(s)}
              className={`capitalize ${filter === s ? 'bg-[#FF5A36] text-white hover:bg-[#E0492B]' : 'border-[#D1CBBF]'}`}
              data-testid={`filter-${s}`}
            >
              {s} {counts[s] > 0 ? `(${counts[s]})` : ''}
            </Button>
          ))}
        </div>

        {/* Partners List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#D1CBBF] p-10 text-center">
            <Building2 className="w-12 h-12 text-[#D1CBBF] mx-auto mb-3" />
            <p className="text-[#4A5568]">No B2B partners found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map(partner => (
              <div key={partner.user_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden" data-testid={`partner-card-${partner.user_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#0A1E40] flex items-center justify-center">
                        {partner.partner_type === 'agent' ? <Briefcase className="w-5 h-5 text-white" /> : <Building2 className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0A1E40] text-sm">{partner.company_name}</h3>
                        <p className="text-xs text-[#4A5568] capitalize">{partner.partner_type === 'agent' ? 'Travel Agent' : 'Corporate'}</p>
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${statusBadge(partner.approval_status)}`}>
                      {partner.approval_status}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-[#4A5568] mb-4">
                    <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {partner.email}</div>
                    <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5" /> {partner.name}</div>
                    {partner.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {partner.phone}</div>}
                    {partner.company_website && <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> {partner.company_website}</div>}
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center mb-4 bg-[#F5F0E6] rounded-xl p-3">
                    <div>
                      <p className="text-lg font-bold text-[#0A1E40]">{partner.total_applications || 0}</p>
                      <p className="text-[10px] text-[#4A5568]">Applications</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[#FF5A36]">{partner.discount_percentage || 0}%</p>
                      <p className="text-[10px] text-[#4A5568]">Discount</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-green-600">{partner.commission_rate || 0}%</p>
                      <p className="text-[10px] text-[#4A5568]">Commission</p>
                    </div>
                  </div>

                  {/* Applications Link */}
                  {partner.total_applications > 0 && (
                    <Link to={`/admin/partners/${partner.user_id}/applications`} className="block mb-3">
                      <Button size="sm" variant="outline" className="w-full border-[#FF5A36] text-[#FF5A36] hover:bg-[#FF5A36]/5" data-testid={`apps-${partner.user_id}`}>
                        <FileText className="w-3.5 h-3.5 mr-1" /> View Applications ({partner.total_applications})
                      </Button>
                    </Link>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    {partner.approval_status === 'pending' && (
                      <>
                        <Button size="sm" className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={() => handleAction(partner.user_id, 'approved')} data-testid={`approve-${partner.user_id}`}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleAction(partner.user_id, 'rejected')} data-testid={`reject-${partner.user_id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {partner.approval_status === 'approved' && (
                      <>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(partner)} data-testid={`settings-${partner.user_id}`}>
                          <DollarSign className="w-3.5 h-3.5 mr-1" /> Settings
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleAction(partner.user_id, 'suspended')} data-testid={`suspend-${partner.user_id}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> Suspend
                        </Button>
                      </>
                    )}
                    {partner.approval_status === 'suspended' && (
                      <Button size="sm" className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={() => handleAction(partner.user_id, 'approved')}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Reactivate
                      </Button>
                    )}
                    {partner.approval_status === 'rejected' && (
                      <Button size="sm" className="flex-1 bg-green-500 hover:bg-green-600 text-white" onClick={() => handleAction(partner.user_id, 'approved')}>
                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Settings Modal */}
        {editPartner && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setEditPartner(null)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="partner-settings-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>Partner Settings — {editPartner.company_name}</h3>
                <button onClick={() => setEditPartner(null)}><X className="w-5 h-5 text-[#4A5568]" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-medium">Discount Percentage (%)</Label>
                  <Input type="number" min="0" max="50" value={editForm.discount_percentage} onChange={e => setEditForm(f => ({ ...f, discount_percentage: parseFloat(e.target.value) || 0 }))} data-testid="discount-input" />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Discount applied to visa fees for this partner's clients</p>
                </div>
                <div>
                  <Label className="text-xs font-medium">Commission Rate (%)</Label>
                  <Input type="number" min="0" max="30" value={editForm.commission_rate} onChange={e => setEditForm(f => ({ ...f, commission_rate: parseFloat(e.target.value) || 0 }))} data-testid="commission-input" />
                  <p className="text-[10px] text-[#9CA3AF] mt-1">Commission earned by partner on completed applications</p>
                </div>
                <div>
                  <Label className="text-xs font-medium">Admin Notes</Label>
                  <Input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Internal notes..." />
                </div>
                <Button onClick={handleSaveSettings} disabled={saving} className="w-full bg-[#FF5A36] hover:bg-[#E0492B]" data-testid="save-settings-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Settings
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminB2BPartners;
